import { NoAccessScreen } from "@/components/no-access-screen";
import { Skeleton } from "@/components/ui/skeleton";
import { AreaProvider } from "@/contexts/area.context";
import { usePermissions } from "@/hooks/use-permissions";
import { READ_ONLY_AREAS, type Area } from "@/lib/permissions";
import { IconLock } from "@tabler/icons-react";
import { useMemo } from "react";

function ReadOnlyBanner() {
  return (
    <div className="flex items-center gap-2 border-b bg-muted/50 px-4 py-2 text-xs text-muted-foreground lg:px-6">
      <IconLock className="size-3.5 shrink-0" />
      <span className="min-w-0">
        Read-only access — creating, editing and deleting are disabled here.
      </span>
    </div>
  );
}

export function AreaRoute({
  area,
  title,
  children,
}: {
  area: Area | null;
  title?: string;
  children: React.ReactNode;
}) {
  const { levelOf, isLoading } = usePermissions();
  const level = area ? levelOf(area) : "write";
  const value = useMemo(() => ({ area, level }), [area, level]);

  // Render nothing decisive until the first answer lands, or every gated page
  // would flash its locked state on a cold load.
  if (area && isLoading) {
    return (
      <div className="space-y-4 p-4 lg:p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (area && level === "none") {
    return <NoAccessScreen area={area} title={title} />;
  }

  // Only read-only areas get the extra wrapper — pages size themselves against
  // SidebarInset, so the layout stays untouched everywhere else.
  if (level === "read" && area && !READ_ONLY_AREAS.includes(area)) {
    return (
      <AreaProvider value={value}>
        <div className="flex h-full min-h-0 flex-col">
          <ReadOnlyBanner />
          <div className="min-h-0 flex-1">{children}</div>
        </div>
      </AreaProvider>
    );
  }

  return <AreaProvider value={value}>{children}</AreaProvider>;
}
